/**
 * Apply existing customer credit (tracker item 55): presentation helpers.
 * The server owns every amount; these only format, default and pre-check
 * input so obvious mistakes are caught before a request.
 */
import * as types from '../../api/types';

/** "Payment" for an allocation made when the payment was recorded, "Credit applied" for credit applied later. */
export function applicationSourceLabel(application: Pick<types.PaymentApplication, 'source'>): string {
  return application.source === 'CREDIT' ? 'Credit applied' : 'Payment';
}

/** Whole cents of a decimal string (avoids float drift when comparing money). */
export function toCents(value: string | number): number {
  return Math.round(Number(value) * 100);
}

/** The amount the form starts with: the smaller of the credit and the balance, 2 decimals. */
export function defaultCreditAmount(credit: string, balance: string): string {
  return (Math.min(toCents(credit), toCents(balance)) / 100).toFixed(2);
}

/** A message when `input` cannot be applied, else null (the server checks again under its locks). */
export function creditAmountError(input: string, credit: string, balance: string): string | null {
  const text = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text) || toCents(text) <= 0) return 'Enter a positive amount with at most 2 decimal places.';
  if (toCents(text) > toCents(credit)) return `That is more than the available credit (${Number(credit).toFixed(2)}).`;
  if (toCents(text) > toCents(balance)) return `That is more than the invoice balance due (${Number(balance).toFixed(2)}).`;
  return null;
}

/** One ACTIVE payment's applications to an invoice, as rows for the invoice page. */
export interface InvoiceApplicationRow {
  applicationId: string;
  paymentId: string;
  paymentReference: string;
  source: types.PaymentApplicationSource;
  date: string;
  amount: string;
}

export function invoiceApplicationRows(payments: types.Payment[], invoiceId: string): InvoiceApplicationRow[] {
  return payments
    .filter((payment) => payment.status === 'ACTIVE')
    .flatMap((payment) => payment.applications
      .filter((application) => application.invoiceId === invoiceId)
      .map((application) => ({
        applicationId: application.id,
        paymentId: payment.id,
        paymentReference: payment.referenceNumber || payment.id.slice(0, 8),
        source: application.source ?? 'PAYMENT',
        date: application.source === 'CREDIT' ? application.appliedOn ?? payment.paymentDate : payment.paymentDate,
        amount: application.amountApplied,
      })))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Label for a payment carrying credit in a picker. */
export function creditPaymentLabel(payment: types.CustomerCreditPayment): string {
  const reference = payment.referenceNumber ? ` ${payment.referenceNumber}` : '';
  return `${payment.paymentMethod.replace('_', ' ')}${reference} of ${payment.paymentDate}: ${Number(payment.unappliedAmount).toFixed(2)} available`;
}
